import api from '../../service/api'
import util from '../../service/util'
import bus from '../../service/bus'

/*
	The core boxart game state: the 16-guess reveal mechanic.

	MECHANIC (per the plan's Global Constraints):
	- 4x4 = 16 tiles, 16 guesses, ONE tile revealed per WRONG guess.
	- The first tile is shown at the start, so `revealed` starts at 1.
	- Hints fire from the SERVER on the 2nd / 6th / 10th / 14th wrong guess
	  (BGG rank -> year -> themes -> designer). We never compute hint text
	  client-side: each guess response may carry a `hint` object we just store.
	- Solve = fewest tiles revealed; the server returns the final result
	  (solved/lost + the answer) and we lock the board.

	Progress is persisted per date in localStorage (`boxart.play.{date}`) so a
	reload resumes the in-progress game. Practice (a past date) persists locally
	too but is unranked: we never block the UI on a server result for it.
*/

var KEY_PREFIX = 'boxart.play.'
var MAX_TILES = 16

// how far back a guest result can be synced onto an account. Older days are
// never offered (and the server enforces the same cap), so a fabricated
// localStorage run can only ever backfill the last few days, not deep history.
// Keep this in step with the same window in controller/api/boxart/result/claim.
var SYNC_WINDOW_DAYS = 5

function storeKey(date) {

	return KEY_PREFIX + date

}

// clamp a tile count into the valid 1..16 range
function clampTiles(value) {

	value = parseInt(value, 10)

	if (!value || value < 1) return 1

	return Math.min(value, MAX_TILES)

}

// a result counts as FINISHED only if it's a genuine win or loss. Guards against
// a mid-game (in-progress) result — e.g. one wrong guess — being treated as a
// finished board, which would flip to the reveal screen showing "out of guesses"
// with no answer. Also heals a snapshot that cached such a bogus result.
function isFinishedResult(r) {

	if (!r) return false

	return !!r.solved || !!r.lost || (r.guesses != null && Number(r.guesses) >= MAX_TILES)

}

// the durable slice of play state — enough to resume an in-progress game
function snapshot(state) {

	return {
		picks: state.picks,
		revealed: state.revealed,
		hints: state.hints,
		result: state.result,
		practice: state.practice,
		// guessed id -> display name, so a reload still labels the guess rows by
		// name instead of falling back to the raw game id
		names: state.names,
		// opaque puzzle-identity token; lets a reload detect a re-curated date
		version: state.version
	}

}

function persist(state) {

	if (!state.date) return

	try {

		var snap = snapshot(state)

		// carry forward a `claimed` flag set by a result sync: snapshot() doesn't
		// track it, so without this a later guess (in-progress day played on after
		// signing in) would wipe it — and the run could then be re-imported into a
		// different account. Once claimed, it stays claimed.
		var prior = restore(state.date)
		if (prior && prior.claimed) snap.claimed = true

		localStorage.setItem(storeKey(state.date), JSON.stringify(snap))

	} catch (e) { /* storage full / disabled — play continues in-memory */ }

}

function restore(date) {

	try {

		var saved = JSON.parse(localStorage.getItem(storeKey(date)))

		return saved || null

	} catch (e) {

		return null

	}

}

/*
	Scan localStorage for REAL daily plays to offer for syncing onto a freshly
	signed-in account. A snapshot is a real attempt iff `practice === false`: a
	genuine same-day play is saved with practice:false, while a past-date archive
	replay is practice:true — so a PRACTICE run is never saved as a real attempt.
	We also skip snapshots already synced (`claimed`) and ones with no guess yet.
	Returns [{ date, picks }], oldest first.
*/
function localRealPlays(includeClaimed) {

	var out = []

	try {

		for (var i = 0; i < localStorage.length; i++) {

			var key = localStorage.key(i)

			if (!key || key.indexOf(KEY_PREFIX) !== 0) continue

			var date = key.slice(KEY_PREFIX.length)

			if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue

			var saved = restore(date)

			if (!saved) continue
			if (saved.practice) continue                       // practice is never a real attempt
			if (saved.claimed && !includeClaimed) continue     // already synced (unless a manual import)
			if (!saved.result) continue                        // only finished live runs are syncable
			if (!saved.picks || !saved.picks.length) continue

			out.push({
				date: date,
				picks: saved.picks,
				// the score the server returned when this finished (resolved on the
				// device), so the sync list can show it
				score: (saved.result && saved.result.score != null) ? saved.result.score : null
			})

		}

	} catch (e) { /* storage disabled — nothing to sync */ }

	out.sort(function(a, b) { return a.date < b.date ? -1 : (a.date > b.date ? 1 : 0) })

	return out

}

// flag a date's local snapshot as synced so it is never re-offered / re-posted
function markClaimed(date) {

	try {

		var saved = restore(date)

		if (saved) {

			saved.claimed = true
			localStorage.setItem(storeKey(date), JSON.stringify(saved))

		}

	} catch (e) { /* storage disabled — server already has it, harmless */ }

}

/*
	Like localRealPlays(), but the PRACTICE snapshots (`practice === true`):
	finished past-date replays held on this device. On sign-in these can be saved
	to the account's separate practice store (boxart_practice), never live
	results. Only finished runs (a stored `result`) are candidates.
*/
function localPracticePlays(includeClaimed) {

	var out = []

	try {

		for (var i = 0; i < localStorage.length; i++) {

			var key = localStorage.key(i)

			if (!key || key.indexOf(KEY_PREFIX) !== 0) continue

			var date = key.slice(KEY_PREFIX.length)

			if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) continue

			var saved = restore(date)

			if (!saved) continue
			if (!saved.practice) continue                      // live plays handled separately
			if (saved.claimed && !includeClaimed) continue     // already synced (unless a manual import)
			if (!saved.result) continue                        // only finished practice runs
			if (!saved.picks || !saved.picks.length) continue

			out.push({
				date: date,
				picks: saved.picks,
				score: (saved.result && saved.result.score != null) ? saved.result.score : null
			})

		}

	} catch (e) { /* storage disabled — nothing to sync */ }

	out.sort(function(a, b) { return a.date < b.date ? -1 : (a.date > b.date ? 1 : 0) })

	return out

}

export default {

	namespaced: true,

	state: {
		// the today/puzzle payload from the server
		today: null,
		// the puzzle date this state belongs to (keys local persistence)
		date: null,
		// guessed game ids, in the order they were tried
		picks: [],
		// guessed game id -> display name (from the chosen suggestion), so the
		// guess list labels rows by name and survives a reload
		names: {},
		// tiles shown; the first is visible from the start
		revealed: 1,
		// unlocked hint objects, in arrival order (server-authored)
		hints: [],
		// set when the game finishes: { solved, answer, score, ... }
		result: null,
		// CURATOR PREVIEW: today's answer payload shipped to an admin who hasn't
		// played, so they see the reveal instead of the play surface. Not a real
		// result (never persisted, unranked) — just enough to render the reveal.
		preview: null,
		// opaque puzzle-identity token from the descriptor; a changed token means
		// the date was re-curated, so any saved local snapshot is discarded
		version: null,
		// a past-date replay — unranked, resolved locally
		practice: false,
		// whether the current finished result is known to be ON the account: true
		// when the server returned it as `locked` (or a signed-in finish/sync just
		// wrote it). A finished result with this false, while signed in, is a
		// local-only play not yet synced.
		onAccount: false,
		// whether the current result was ever recorded to an account (the local
		// snapshot is flagged claimed, or it loaded as an account result). Unlike
		// onAccount this survives logout, so we can tell a genuine guest play from
		// one completed while signed in (e.g. when viewing it logged out).
		claimed: false,
		// in-flight flag for load()/guess()
		loading: false
	},

	getters: {

		today: function(state) {

			return state.today

		},

		tilesRevealed: function(state) {

			return state.revealed

		},

		guessesUsed: function(state) {

			return state.picks.length

		},

		remaining: function(state) {

			return MAX_TILES - state.picks.length

		},

		finished: function(state) {

			return !!state.result

		},

		// a finished result that is NOT on the account: not server-locked
		// (onAccount) AND not already recorded to an account (claimed). Without the
		// claimed check, a result completed while signed in and then viewed after a
		// guest reload (onAccount reset to false) would wrongly read as unsynced
		// once signed back in, even though it's on the account.
		unsynced: function(state) {

			return !!state.result && !state.onAccount && !state.claimed

		},

		// wrong guesses so far (every pick but a final solving one). Mirrors the
		// reveal count growth: revealed = 1 + wrongCount (capped at 16).
		wrongCount: function(state) {

			return Math.max(0, state.revealed - 1)

		},

		hints: function(state) {

			return state.hints

		},

		result: function(state) {

			return state.result

		},

		// guessed id -> display name map (for labelling the guess rows)
		names: function(state) {

			return state.names

		},

		// the curator-preview answer (admin, today, not yet played), else null
		preview: function(state) {

			return state.preview

		},

		loading: function(state) {

			return state.loading

		}

	},

	mutations: {

		loading: function(state, value) {

			state.loading = !!value

		},

		// drop the current puzzle so the loader shows while a new one loads
		// (a guess never calls this — only load() does)
		clear: function(state) {

			state.today = null
			state.date = null
			state.picks = []
			state.names = {}
			state.revealed = 1
			state.hints = []
			state.result = null
			state.version = null
			state.practice = false
			state.preview = null
			state.onAccount = false
			state.claimed = false

		},

		// seed a fresh game (no saved progress)
		init: function(state, data) {

			data = data || {}

			var locked = isFinishedResult(data.result) ? data.result : null

			state.today = data.today || null
			state.date = data.date || null
			state.practice = !!data.practice
			state.version = (data.today && data.today.version) || null
			state.hints = []
			state.names = {}
			state.result = locked
			// a server `locked` result is, by definition, already on the account
			state.onAccount = !!locked
			// init is the no-saved-snapshot path, so the only "recorded to an
			// account" signal is an account result
			state.claimed = !!locked

			// a server-locked (already finished) board has no local progress to
			// resume — seed the real tile/guess counts from the locked result so
			// the getters report the true TILES/GUESSES (not 1/16 and 0/16) and
			// the share grid matches, even cross-device or after a storage loss
			if (locked) {

				state.revealed = clampTiles(locked.tiles)
				state.picks = (locked.picks || []).slice()

			} else {

				state.picks = []
				state.revealed = 1

			}

		},

		// rehydrate from a saved snapshot, keeping the freshly-loaded puzzle
		// payload (image / number / date) the server just returned
		restore: function(state, data) {

			data = data || {}

			var saved = data.saved || {}
			// a server-locked board takes precedence over the local snapshot,
			// which may pre-date the finish (e.g. solved on another device). Both
			// are gated: only a genuinely finished result locks the board, so a
			// mid-game result (from the server OR a previously-cached bogus one)
			// resumes play instead of flashing the answerless reveal screen.
			var locked = isFinishedResult(data.result) ? data.result : null
			var savedResult = isFinishedResult(saved.result) ? saved.result : null

			state.today = data.today || null
			state.date = data.date || null
			state.practice = data.practice != null ? !!data.practice : !!saved.practice
			// prefer the freshly-loaded descriptor's token (authoritative)
			state.version = (data.today && data.today.version) || saved.version || null
			state.hints = saved.hints || []
			// restore the guessed-name labels so a reload mid-game keeps showing
			// the game names instead of falling back to the raw ids
			state.names = saved.names || {}
			// the server-locked result (authoritative) wins over a local snapshot,
			// which can be stale (e.g. saved before a fix, or for an old answer)
			state.result = locked || savedResult || null
			// only a SERVER result is known to be on the account; a result coming
			// from the local snapshot is a local-only play not yet synced
			state.onAccount = !!locked
			// recorded to an account if it's an account result, or the local
			// snapshot was flagged claimed (saved to an account before, e.g. a day
			// completed while signed in and now viewed logged out)
			state.claimed = !!locked || !!(saved && saved.claimed)

			if (locked) {

				// seed the real tile/guess counts so the getters and share grid
				// report the finished numbers, not a stale in-progress snapshot
				state.revealed = clampTiles(locked.tiles)
				state.picks = (locked.picks || []).slice()

			} else {

				state.picks = saved.picks || []
				state.revealed = saved.revealed || 1

			}

		},

		pick: function(state, gameId) {

			state.picks.push(gameId)

		},

		// remember a guessed game's display name (id -> name). Replace the map so
		// the new key is reactive without a Vue.set dependency.
		name: function(state, payload) {

			if (!payload || !payload.id || !payload.name) return

			var next = {}
			for (var k in state.names) next[k] = state.names[k]
			next[payload.id] = payload.name

			state.names = next

		},

		// a wrong guess reveals exactly one more tile, capped at 16
		reveal: function(state) {

			state.revealed = Math.min(state.revealed + 1, MAX_TILES)

		},

		hint: function(state, hint) {

			if (hint) state.hints.push(hint)

		},

		result: function(state, result) {

			state.result = result || null

		},

		// mark the current finished result as on the account (a signed-in live
		// finish wrote it, or a sync just saved it), clearing the unsynced nudge.
		// It's also, by definition, now recorded to an account.
		accounted: function(state) {

			state.onAccount = true
			state.claimed = true

		},

		preview: function(state, answer) {

			state.preview = answer || null

		}

	},

	actions: {

		/*
			Load today's puzzle (or a past date for practice). Resumes saved
			progress when present, else starts fresh. If the server reports the
			board is already locked (`today.locked`), seed the result from it.
		*/
		load: function(context, date) {

			// a past date is loaded via the practice (puzzle) endpoint; whether it
			// actually counts as PRACTICE depends on the response (below)
			var isPast = !!date

			// drop the previous puzzle immediately so the loader shows (and a
			// stale puzzle never lingers when switching from the archive)
			context.commit('clear')
			context.commit('loading', true)

			var endpoint = isPast ? 'puzzle' : 'today'
			var params = isPast ? { date: date } : {}

			return api.request(endpoint, params).then(function(json) {

				context.commit('loading', false)

				if (!json || json.error) return json

				var puzzleDate = json.date || date || null
				// only a genuinely finished result locks the board — an in-progress
				// result row must not render as a finished one (it would show the
				// reveal/"out of guesses" screen with no answer); resume play instead.
				var locked = isFinishedResult(json.locked) ? json.locked : null

				// a past date the account holds as a PRACTICE result (no live result)
				// comes back as `lockedPractice`: shown greyed (practice) but still
				// ON the account, so no "not synced" nudge
				var lockedPractice = (!locked && json.lockedPractice) ? json.lockedPractice : null

				// either is an account-held result for this date
				var accountResult = locked || lockedPractice

				var saved = puzzleDate ? restore(puzzleDate) : null

				// a re-curated date changes the puzzle identity token; a snapshot
				// saved under the OLD token is stale (it may even hold a finished
				// board for the previous game), so drop it and start the new puzzle.
				// A legacy snapshot with no token (saved.version undefined) is also
				// treated as stale once the server starts sending a token.
				if (saved && json.version && saved.version !== json.version) {

					saved = null

				}

				// classify the view by what the result actually IS, not just the
				// route, so a genuine local play isn't mislabelled practice:
				// - account LIVE result -> real, scored
				// - account PRACTICE result -> practice (greyed), on-account
				// - a finished LOCAL result not yet on the account -> its true nature
				//   (a genuine live play stays a REAL result, just unsynced)
				// - otherwise (a fresh / in-progress past date) -> practice
				var practice
				if (locked) practice = false
				else if (lockedPractice) practice = true
				else if (saved && saved.result) practice = !!saved.practice
				else practice = isPast

				if (saved) {

					context.commit('restore', {
						today: json,
						date: puzzleDate,
						practice: practice,
						saved: saved,
						result: accountResult
					})

				} else {

					context.commit('init', {
						today: json,
						date: puzzleDate,
						practice: practice,
						result: accountResult
					})

				}

				// an account-held result (live or practice) persists too, so a
				// reload after the daily reset doesn't re-fetch into a fresh game.
				// It's on the account, so flag it claimed: it shouldn't be counted
				// as syncable or offered for sync (and not spread to another account).
				if (accountResult) {

					persist(context.state)
					markClaimed(puzzleDate)

				}

				// CURATOR PREVIEW: an admin who hasn't played today gets the answer
				// up front so the reveal shows instead of the play surface. Never
				// persisted (it's not a real result) and only on the live daily.
				if (!accountResult && !practice && json.preview && json.answer) {

					context.commit('preview', json.answer)

				}

				return json

			})

		},

		/*
			Submit a guess. We send the running picks (the picks made BEFORE this
			one) so the server can validate position / award hints deterministically.
			On the response:
			- wrong  -> reveal one more tile.
			- hint   -> store the server-authored hint object.
			- solved/lost -> set the result (with the revealed answer) and lock.
			Progress is persisted to localStorage after every guess.
		*/
		guess: function(context, payload) {

			var state = context.state

			// payload may be a bare game id (a normal guess) or { id, skip }.
			// A skip burns a guess and reveals a tile without naming a game; it
			// is stored as id 0 and the server is told via the `skip` flag.
			var skip = !!(payload && typeof payload === 'object' && payload.skip)
			var gameId = skip ? 0 : ((payload && typeof payload === 'object') ? payload.id : payload)

			// board already locked (finished) — ignore further guesses
			if (state.result) return Promise.resolve(state.result)

			// picks BEFORE this guess, for server-side validation
			var previous = state.picks.slice()

			context.commit('pick', gameId)

			// remember the picked game's name so the guess row stays labelled by
			// name across a reload (a skip has no name; it renders as "Skipped")
			if (!skip && gameId > 0 && payload && typeof payload === 'object' && payload.name) {

				context.commit('name', { id: gameId, name: payload.name })

			}

			context.commit('loading', true)

			return api.request('guess', {
				date: state.date,
				game_id: gameId,
				picks: previous,
				skip: skip
			}).then(function(json) {

				context.commit('loading', false)

				json = json || {}

				if (json.error) return json

				if (!json.correct) context.commit('reveal')

				if (json.hint) context.commit('hint', json.hint)

				if (json.solved || json.lost) {

					context.commit('result', {
						solved: !!json.solved,
						lost: !!json.lost,
						answer: json.answer || null,
						score: json.score != null ? json.score : null
					})

				}

				persist(context.state)

				// a signed-in finish is recorded to the account by the server: a live
				// (today) finish into boxart_result, a past-date (practice) finish
				// into boxart_practice. Either way it's now on the account, so flag
				// the local snapshot claimed (it can't later be offered for sync into
				// a DIFFERENT account) and clear the "not synced" nudge. persist()
				// carries the claimed flag forward.
				if ((json.solved || json.lost) && context.rootGetters['account/signedIn']) {

					markClaimed(context.state.date)
					context.commit('accounted')

				}

				return json

			})

		},

		/*
			On sign-in, reconcile this browser's LOCAL real plays against the
			results already on the account. Any local play whose date the account
			doesn't have is a "gap"; we hand the gap to the sync prompt (App.vue
			hosts it) so the player can choose to save them. Practice replays are
			excluded by localRealPlays(), so they are never offered as real plays.

			Cheap and safe to dispatch on every sign-in: it no-ops when there are
			no local real plays, and only one read (result/dates) when there are.
		*/
		/*
			How many local device plays (live + practice, within the last
			SYNC_WINDOW_DAYS, not yet synced) could be synced onto the account.
			Local-only (no server call), so it's cheap to check when deciding
			whether to surface the "Sync device plays" menu item. Returns a number.
		*/
		deviceSyncable: function() {

			var today = util.puzzleDate()
			var cutoff = new Date(Date.parse(today + 'T00:00:00Z') - SYNC_WINDOW_DAYS * 86400000).toISOString().slice(0, 10)

			var n = 0

			localRealPlays(false).forEach(function(p) { if (p.date >= cutoff) n++ })
			localPracticePlays(false).forEach(function(p) { if (p.date >= cutoff) n++ })

			return n

		},

		/*
			Compute the SYNC GAP: the recent local device plays the account doesn't
			already hold, tagged live vs practice, chronological. Returns a Promise
			of the gap array (no UI side effects — the dialog opens itself and calls
			this so it can show a loading state during the result/dates round trip).

			opts.force re-offers plays even if a previous sync flagged them claimed
			(a manual sync); the automatic on-sign-in path omits it so it neither
			nags nor spreads a play across accounts.
		*/
		syncGap: function(context, opts) {

			var force = !!(opts && opts.force)

			var live = localRealPlays(force)
			var practice = localPracticePlays(force)

			if (!live.length && !practice.length) return Promise.resolve([])

			return api.request('result/dates', {}).then(function(json) {

				var haveLive = {}
				var havePractice = {}

				if (json && json.dates) {

					json.dates.forEach(function(date) { haveLive[date] = true })

				}

				if (json && json.practiceDates) {

					json.practiceDates.forEach(function(date) { havePractice[date] = true })

				}

				// the earliest claimable date (today minus the window); older local
				// plays are never offered, matching the server's cap
				var today = util.puzzleDate()
				var cutoff = new Date(Date.parse(today + 'T00:00:00Z') - SYNC_WINDOW_DAYS * 86400000).toISOString().slice(0, 10)

				// the gap: recent local plays the account doesn't already hold, each
				// tagged with the store it belongs in (live results vs practice)
				var gap = []

				live.forEach(function(play) {

					if (!haveLive[play.date] && play.date >= cutoff) {

						gap.push({ date: play.date, picks: play.picks, mode: 'live', score: play.score })

					}

				})

				practice.forEach(function(play) {

					if (!havePractice[play.date] && play.date >= cutoff) {

						gap.push({ date: play.date, picks: play.picks, mode: 'practice', score: play.score })

					}

				})

				// chronological across both stores (the gatherers sort within each,
				// but the combined list must read in date order)
				gap.sort(function(a, b) { return a.date < b.date ? -1 : (a.date > b.date ? 1 : 0) })

				return gap

			})

		},

		/*
			Save the confirmed gap to the account. The server re-validates every
			entry against the real answer (never trusting the client outcome),
			skips dates already on record, and folds the new history into the
			player's streak. On success we flag each synced date's local snapshot
			so it is never re-offered, and signal the reveal's stats to refresh.
		*/
		sync: function(context, entries) {

			entries = entries || []

			if (!entries.length) return Promise.resolve(null)

			// split by store: live results vs practice runs (default live)
			var live = []
			var practice = []

			entries.forEach(function(entry) {

				var item = { date: entry.date, picks: entry.picks }

				if (entry.mode === 'practice') practice.push(item)
				else live.push(item)

			})

			return api.request('result/claim', { entries: live, practice: practice }).then(function(json) {

				if (!json || json.error) return json

				// every date now safely on the account (live synced/skipped, or
				// practice synced) — stop offering them locally
				var done = (json.synced || []).concat(json.skipped || []).concat(json.practiceSynced || [])

				done.forEach(markClaimed)

				// if the puzzle currently on screen was among them, clear its
				// "not synced" nudge straight away
				if (context.state.date && done.indexOf(context.state.date) !== -1) {

					context.commit('accounted')

				}

				// let the end-screen stats panel re-fetch now the runs are saved
				bus.$emit('claimed', json)

				return json

			})

		}

	}

}
